'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLocale } from 'next-intl';
import type { ComponentProps } from 'react';

/**
 * LocalizedLink - A wrapper around Next.js Link that automatically adds locale prefix
 * 
 * Handles:
 * - Internal links: Automatically prepends locale (e.g., /hub -> /en/hub)
 * - External links: Leaves as-is (http://, https://)
 * - Hash/anchor links: Leaves as-is (#section)
 * - Already localized links: Leaves as-is if already has locale prefix
 * 
 * @example
 * <LocalizedLink href="/hub">Hub</LocalizedLink>
 * <LocalizedLink href="https://example.com">External</LocalizedLink>
 * <LocalizedLink href="#section">Anchor</LocalizedLink>
 */
export function LocalizedLink({
  href,
  ...props
}: ComponentProps<typeof Link>) {
  const pathname = usePathname();
  const locale = useLocale();

  /**
   * Check if a path should be localized
   */
  const shouldLocalizePath = (path: string): boolean => {
    // Don't localize external links
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return false;
    }
    // Don't localize hash/anchor links
    if (path.startsWith('#')) {
      return false;
    }
    // Don't localize mailto links
    if (path.startsWith('mailto:')) {
      return false;
    }
    // Don't localize if already has locale prefix
    if (path.startsWith(`/${locale}/`) || path === `/${locale}`) {
      return false;
    }
    return true;
  };

  /**
   * Get localized path by prepending locale prefix
   */
  const getLocalizedPath = (path: string): string => {
    if (!shouldLocalizePath(path)) {
      return path;
    }
    // Remove leading slash if present, then add locale and slash
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `/${locale}/${cleanPath}`;
  };

  // Handle external links (http://, https://)
  if (typeof href === 'string' && (href.startsWith('http://') || href.startsWith('https://'))) {
    return <Link href={href} {...props} />;
  }

  // Handle hash/anchor links (#section)
  if (typeof href === 'string' && href.startsWith('#')) {
    return <Link href={href} {...props} />;
  }

  // Handle mailto links
  if (typeof href === 'string' && href.startsWith('mailto:')) {
    return <Link href={href} {...props} />;
  }

  // Handle object hrefs (Next.js Link object format)
  if (typeof href === 'object' && href !== null) {
    const localizedHref = {
      ...href,
      pathname: href.pathname && shouldLocalizePath(href.pathname)
        ? getLocalizedPath(href.pathname)
        : href.pathname,
    };
    return <Link href={localizedHref} {...props} />;
  }

  // Handle string hrefs - localize internal paths
  if (typeof href === 'string') {
    const localizedHref = shouldLocalizePath(href)
      ? getLocalizedPath(href)
      : href;
    return <Link href={localizedHref} {...props} />;
  }

  // Fallback
  return <Link href={href} {...props} />;
}


















